import type { MarketingAttribution } from '../tracking/attribution';
import { isProductionHost } from '../host-redirect';

import { type LeadPromptOffer, leadPromptOffer } from './lead-prompt';

/** Our sales WhatsApp number in international digits, e.g. 9665XXXXXXXX. */
export const SALES_WHATSAPP_NUMBER = '';

const MARKETING_HOSTS = new Set(['askflow.sa', 'www.askflow.sa']);

/**
 * White-label deployments (Deem: `deem-padel`) default their homepage to
 * their own tenant. The committed .env sets AskFlow's own `flow`, so only a
 * different value marks a white-label build.
 */
export function isWhiteLabelBuild(defaultLandingPage: string | undefined): boolean {
  return Boolean(defaultLandingPage) && defaultLandingPage !== 'flow';
}

/**
 * Whether AskFlow's own sales widgets (the WhatsApp button and lead prompt)
 * may render. The (marketing) routes answer on every host, including tenant
 * domains and white-label deployments such as Deem (deempadel.com). Local and
 * preview hosts stay on for review, except on a white-label build.
 */
export function isAskflowSalesHost(host: string, whiteLabelBuild: boolean): boolean {
  if (whiteLabelBuild) return false;

  const hostname = host.split(':')[0]?.toLowerCase() ?? '';

  return MARKETING_HOSTS.has(hostname) || !isProductionHost(hostname);
}

// No 0/O or 1/I, so a reference read aloud or retyped survives.
const REFERENCE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

/** A short code the visitor sends with their first message, e.g. AF-7K3P. */
export function newWhatsAppReference(random: () => number = Math.random): string {
  let code = '';

  for (let index = 0; index < 4; index++) {
    code += REFERENCE_ALPHABET[Math.floor(random() * REFERENCE_ALPHABET.length)];
  }

  return `AF-${code}`;
}

const TOPICS: Record<LeadPromptOffer, { en: string; ar: string }> = {
  default: { en: 'AskFlow', ar: 'AskFlow' },
  padel: { en: 'AskFlow for my padel club', ar: 'AskFlow لنادي البادل' },
  studio: { en: 'AskFlow for my studio', ar: 'AskFlow للاستوديو' },
  gym: { en: 'AskFlow for my gym', ar: 'AskFlow للنادي الرياضي' },
};

export function whatsAppGreeting(pathname: string, locale: 'en' | 'ar', reference: string | null): string {
  const topic = TOPICS[leadPromptOffer(pathname)][locale];
  const text = locale === 'ar' ? `مرحباً، أبغى أعرف أكثر عن ${topic}` : `Hi, I'd like to know more about ${topic}`;

  if (!reference) return text;

  return locale === 'ar' ? `${text} (رمز: ${reference})` : `${text} (ref: ${reference})`;
}

export function whatsAppChatUrl(number: string, text: string): string {
  return `https://wa.me/${number.replace(/\D/g, '')}?text=${encodeURIComponent(text)}`;
}

/** Where the visit came from, compact enough for an analytics property. */
export function whatsAppClickSource(attribution: MarketingAttribution | null): string {
  if (!attribution) return 'unknown';
  if (attribution.utm_source) return attribution.utm_source.slice(0, 60);
  if (attribution.gclid || attribution.gbraid || attribution.wbraid) return 'google_ads';
  if (attribution.fbclid) return 'meta_ads';
  if (attribution.li_fat_id) return 'linkedin_ads';
  if (attribution.referrer) {
    try {
      return new URL(attribution.referrer).hostname;
    } catch {
      return 'referral';
    }
  }

  return 'direct';
}
